import HologardeBackground from '@assets/hologarde-background.jpg';
import { Center, Flex, For, Grid, GridItem, Image, Spinner, Text } from '@chakra-ui/react';
import CustomTitle from '@components/common/CustomTitle';
import MouseFocus from '@components/common/MouseFocus';
import InactivityModal from '@components/InactivityModal';
import { MapWrapper } from '@components/map/MapWrapper';
import ReplayTimelineWrapper from '@components/replay/ReplayTimelineWrapper';
import Search from '@components/replay/search/Search';
import CameraControlWrapper from '@components/video/cameraControl/CameraControlWrapper';
import JoystickControl from '@components/video/cameraControl/JoystickControl';
import PerimeterDoubtCheckGrid from '@components/video/PerimeterDoubtCheckGrid';
import PerimeterGrid from '@components/video/PerimeterGrid';
import { useAppSelector } from '@hooks/redux.hooks';
import { useAuth } from '@hooks/useAuth';
import { selectActiveUser } from '@redux/authent/authent.selectors';
import { selectActiveGlobalScreenConfiguration } from '@redux/config/config.selectors';
import { selectJoystickControlId } from '@redux/global/global.selector';
import { selectContext } from '@redux/settings/settings.selectors';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetSituationDiffQuery } from '@services/c2/sse.api';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useParams } from 'react-router';

import { GridItem as GridItemType, GridItemComponentEnum } from '@/types/config/gridLayout.types';

import StreamGrid from '../video/StreamGrid';

function displayComponent(item: GridItemType) {
  switch (item.component) {
    case GridItemComponentEnum.MAP: {
      return <MapWrapper selectedMap={item.selectedMap} />;
    }
    case GridItemComponentEnum.CAMERA_CONTROL: {
      return <CameraControlWrapper defaultSelectedCameraKey={item.selectedCamera} uniqueKey={item.i} />;
    }
    case GridItemComponentEnum.DYNAMIC_CAMERA_CONTROL: {
      return <CameraControlWrapper isDynamic defaultSelectedCameraKey={item.selectedCamera} uniqueKey={item.i} />;
    }
    case GridItemComponentEnum.CAMERA_REPLAY: {
      return <CameraControlWrapper replayMode uniqueKey={item.i} />;
    }
    case GridItemComponentEnum.LAD_STREAM_GRID: {
      return (
        <StreamGrid
          values={item.selectedStreams}
          innerGridWidth={item.innerGridWidth}
          innerGridHeight={item.innerGridHeight}
          uniqueKey={item.i}
        />
      );
    }
    case GridItemComponentEnum.PERIMETER_STREAM_GRID: {
      return (
        <PerimeterGrid innerGridWidth={item.innerGridWidth} innerGridHeight={item.innerGridHeight} uniqueKey={item.i} />
      );
    }
    case GridItemComponentEnum.PERIMETER_DOUBT_CHECK: {
      return <PerimeterDoubtCheckGrid uniqueKey={item.i} />;
    }
    case GridItemComponentEnum.TIME_BAR: {
      return <ReplayTimelineWrapper key={item.i} />;
    }
    case GridItemComponentEnum.SEARCH: {
      return <Search />;
    }
    default:
      return item.component;
  }
}

export default function DisplayGrid() {
  const { code: screenConfigurationCode } = useParams();
  const { token } = useAuth();
  const location = useLocation();
  const { formatMessage } = useIntl();
  const joystickControlId = useAppSelector(selectJoystickControlId);
  const { activeGroup, activeSite } = useAppSelector(selectActiveUser);
  const inactivityLimit = activeGroup?.inactivityTime ?? null;
  const currentContext = useAppSelector(selectContext);
  const activeGlobalScreenConfiguration = useAppSelector(selectActiveGlobalScreenConfiguration);
  const screenConfiguration =
    activeGlobalScreenConfiguration?.screenConfigurations.find(
      (screenConfig) => screenConfig.code === screenConfigurationCode && screenConfig.context === currentContext,
    ) ?? null;
  const gridLayout = screenConfiguration?.gridLayout ?? null;

  useGetSituationDiffQuery(
    activeSite && token
      ? {
          site: activeSite.code,
          platforms: activeGroup ? activeGroup.platforms : [],
          token,
        }
      : skipToken,
    {
      refetchOnMountOrArgChange: true,
    },
  );

  const configErrorLabel =
    screenConfiguration === null ? (
      <FormattedMessage id="screenConfiguration.noConfiguration" />
    ) : (
      <FormattedMessage
        id="screenConfiguration.noLayoutContext"
        values={{ context: formatMessage({ id: `screenConfiguration.context.${currentContext}` }) }}
      />
    );

  if (gridLayout === null || screenConfiguration === null) {
    return (
      <Flex width="100%" height="100%" alignContent="center" justifyContent="center">
        <CustomTitle color="blue.900">
          <Text fontWeight="medium" textStyle="lg" lineClamp={1} userSelect="none">
            <FormattedMessage id={`routes.${location.pathname}`} />
          </Text>
        </CustomTitle>
        <Image src={HologardeBackground} fit="cover" width="100%" height="100%" position="absolute" />
        <Center zIndex={1} color="neutral.white">
          {activeGlobalScreenConfiguration === null ? <Spinner size="xl" color="neutral.white" /> : configErrorLabel}
        </Center>
      </Flex>
    );
  }

  return (
    <>
      {joystickControlId !== null && <JoystickControl />}
      <MouseFocus />
      {inactivityLimit !== null && <InactivityModal inactivityLimit={inactivityLimit} />}
      <Grid
        templateRows={`repeat(${gridLayout.nbRow}, 1fr)`}
        templateColumns={`repeat(${gridLayout.nbCol}, 1fr)`}
        width="100%"
        height="100%"
        position="relative"
        backgroundColor="neutral.black"
      >
        <For each={gridLayout.items}>
          {(item) => (
            <GridItem
              key={item.i}
              backgroundColor="transparent"
              position="relative"
              rowSpan={item.h}
              colSpan={item.w}
              minW={item.minW ?? undefined}
              minH={item.minH ?? undefined}
              rowStart={item.y + 1}
              colStart={item.x + 1}
              width="100%"
              height="100%"
            >
              {displayComponent(item)}
            </GridItem>
          )}
        </For>
      </Grid>
    </>
  );
}
